"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Compass, Hammer, Zap, Layers, House as Home, FileCheck, Landmark, ShieldAlert, ArrowRight, TrendingUp } from 'lucide-react';
const costVsValueData = [{
  project: "Minor kitchen remodel (midrange)",
  cost: "$28,359",
  resaleValue: "$31,712",
  recouped: "111.8%"
}, {
  project: "Major kitchen remodel (midrange)",
  cost: "$79,591",
  resaleValue: "$41,805",
  recouped: "52.5%"
}, {
  project: "Bath remodel (midrange)",
  cost: "$25,304",
  resaleValue: "$19,595",
  recouped: "77.4%"
}, {
  project: "Bath remodel (upscale)",
  cost: "$79,717",
  resaleValue: "$36,128",
  recouped: "45.3%"
}, {
  project: "Basement remodel",
  cost: "$49,681",
  resaleValue: "$45,636",
  recouped: "91.9%"
}, {
  project: "Deck addition (composite)",
  cost: "$24,984",
  resaleValue: "$20,264",
  recouped: "81.1%"
}, {
  project: "Primary suite addition (midrange)",
  cost: "$162,109",
  resaleValue: "$59,369",
  recouped: "36.6%"
}, {
  project: "Backyard patio",
  cost: "$50,928",
  resaleValue: "$21,107",
  recouped: "41.4%"
}];
export default function RemodelingCostDrivers() {
  return <section id="remodelingcostdrivers" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs tracking-wider uppercase font-medium">
            Project Planning & Investment Insights
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
            Understanding the Factors That Shape Your Renovation Investment
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every home alteration involves unique architectural variables, existing structural realities,
            and municipal compliance requirements. Exploring these fundamental cost drivers early helps set realistic
            expectations and guides balanced decision-making throughout planning and construction.
          </p>
        </div>

        {/* Primary Drivers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {/* Card 1: Project Scope */}
          <Card className="border border-border/80 bg-card/80 backdrop-blur-xs rounded-xl shadow-xs transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                <Compass className="size-5 text-chart-1" />
              </div>
              <CardTitle className="font-serif text-xl font-semibold text-foreground">
                Overall Project Scope & Footprint
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                The fundamental scope of work dictates baseline labor hours, logistical complexity, and material volumes.
                Confining alterations within an existing footprint requires different resource allocations than expanding
                outward or adding a second story.
              </p>
              <p>
                Ground-level extensions require grading, foundation excavation, and exterior envelope framing, while interior
                reconfigurations prioritize demolition sequencing and structural load recalculations.
              </p>
            </CardContent>
          </Card>

          {/* Card 2: Structural Modifications */}
          <Card className="border border-border/80 bg-card/80 backdrop-blur-xs rounded-xl shadow-xs transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                <Hammer className="size-5 text-chart-1" />
              </div>
              <CardTitle className="font-serif text-xl font-semibold text-foreground">
                Structural Load & Layout Changes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Removing interior partition walls to achieve an open-concept living space often requires engineered steel or
                LVL headers, temporary ceiling shoring, and point-load distribution down through crawlspaces or basements.
              </p>
              <p>
                Vaulting ceilings, widening exterior door spans for multi-slide units, or reinforcing roof trusses for new
                dormers involve specialized structural engineering calculations and precise framing execution.
              </p>
            </CardContent>
          </Card>

          {/* Card 3: Mechanical, Electrical & Plumbing */}
          <Card className="border border-border/80 bg-card/80 backdrop-blur-xs rounded-xl shadow-xs transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                <Zap className="size-5 text-chart-1" />
              </div>
              <CardTitle className="font-serif text-xl font-semibold text-foreground">
                Mechanical & Utility Modernization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Relocating fixtures across rooms demands extensive rerouting of water supply lines, waste stacks, and vent
                piping. Modern high-output kitchens and spa bathrooms frequently necessitate upgraded electrical main service panels.
              </p>
              <p>
                Reconfiguring floor plans also impacts HVAC load calculations, requiring new duct trunk lines, return air returns,
                or dedicated zoned heat pump systems to maintain balanced thermal comfort.
              </p>
            </CardContent>
          </Card>

          {/* Card 4: Finish Levels */}
          <Card className="border border-border/80 bg-card/80 backdrop-blur-xs rounded-xl shadow-xs transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                <Layers className="size-5 text-chart-1" />
              </div>
              <CardTitle className="font-serif text-xl font-semibold text-foreground">
                Material & Finish Selections
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Surface selections generate significant variances in overall allocation. Custom inset cabinetry with tailored
                organizers involves greater joinery craftsmanship than semi-custom modular lines.
              </p>
              <p>
                Natural stone slabs, handmade zellige tiles, architectural-grade plumbing trim, and custom wood millwork
                demand higher material investment and specialized, detail-intensive installation expertise.
              </p>
            </CardContent>
          </Card>

          {/* Card 5: Decatur Housing Realities */}
          <Card className="border border-border/80 bg-card/80 backdrop-blur-xs rounded-xl shadow-xs transition-all hover:shadow-md md:col-span-2 lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground mb-3">
                <Home className="size-5 text-chart-1" />
              </div>
              <CardTitle className="font-serif text-xl font-semibold text-foreground">
                Conditions Specific to Historic & Mid-Century Decatur Residences
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm leading-relaxed text-muted-foreground">
              <div>
                <h3 className="font-serif font-medium text-foreground text-base mb-1.5">
                  Craftsman Bungalows & Victorians
                </h3>
                <p>
                  Homes built in the early 20th century frequently present uninsulated framing bays, out-of-square framing,
                  undersized joists, remnant knob-and-tube wiring, and aging cast-iron sewer lines that must be brought into code compliance.
                </p>
              </div>
              <div>
                <h3 className="font-serif font-medium text-foreground text-base mb-1.5">
                  Mid-Century Brick Ranches
                </h3>
                <p>
                  Typical 1950s and 1960s ranches often feature low-pitch roofs, slab foundations or low-clearance crawlspaces,
                  galvanized plumbing, and load-bearing central masonry walls that call for creative structural remediation.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Atlanta Remodeling Cost vs Value (2025) Table */}
        <div className="rounded-xl border border-border/80 bg-card/80 backdrop-blur-xs p-6 sm:p-8 shadow-xs mb-16 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-foreground shrink-0">
                <TrendingUp className="size-5 text-chart-1" />
              </div>
              <div>
                <Badge variant="outline" className="mb-1 text-xs uppercase font-medium">
                  Market Benchmark
                </Badge>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Atlanta remodeling cost vs value (2025)
                </h3>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <caption className="text-xs text-muted-foreground text-left py-2 font-normal">
                National survey averages for the Atlanta market, not quotes
              </caption>
              <thead>
                <tr className="border-b border-border text-foreground font-semibold text-sm">
                  <th scope="col" className="py-3.5 px-4 font-serif">Project</th>
                  <th scope="col" className="py-3.5 px-4 font-serif text-right sm:text-left">Average Cost</th>
                  <th scope="col" className="py-3.5 px-4 font-serif text-right sm:text-left">Resale Value Added</th>
                  <th scope="col" className="py-3.5 px-4 font-serif text-right">Cost Recouped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-sm">
                {costVsValueData.map((item, i) => <tr key={item.project} data-index={i} className="hover:bg-muted/40 transition-colors">
                    <td className="py-3.5 px-4 font-medium text-foreground">
                      {item.project}
                    </td>
                    <td className="py-3.5 px-4 text-muted-foreground text-right sm:text-left font-mono text-xs sm:text-sm">
                      {item.cost}
                    </td>
                    <td className="py-3.5 px-4 text-muted-foreground text-right sm:text-left font-mono text-xs sm:text-sm">
                      {item.resaleValue}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-xs sm:text-sm font-semibold text-chart-1">
                      {item.recouped}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border/60">
            Source: Remodeling Magazine / JLC Cost vs Value Report 2025, Atlanta, GA metro.
          </p>
        </div>

        {/* Permitting & Regulatory Section */}
        <div className="rounded-xl border border-border bg-secondary/30 p-8 sm:p-10 mb-16">
          <div className="max-w-3xl mb-8">
            <Badge variant="outline" className="mb-3 text-xs uppercase font-medium">
              Regulatory Frameworks
            </Badge>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground tracking-tight mb-3">
              Jurisdiction Permitting & Historic District Review
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Timelines and documentation requirements diverge significantly depending on whether a property sits within the
              City of Decatur municipal limits or unincorporated DeKalb County, as well as designated local historic districts.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Column 1: City of Decatur */}
            <div className="p-6 rounded-lg bg-card border border-border/60 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <FileCheck className="size-5 text-chart-1" />
                <h4 className="font-serif font-semibold text-foreground text-lg">City of Decatur</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Enforces comprehensive tree canopy protection ordinances, strict lot coverage and impervious surface ratios,
                and precise stormwater management plans. Plan reviews involve multiple department checkpoints before issuance.
              </p>
              <ul className="text-xs text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>Arborist site evaluations and tree protection bonds</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>Rigorous building code and energy efficiency compliance</span>
                </li>
              </ul>
            </div>

            {/* Column 2: DeKalb County */}
            <div className="p-6 rounded-lg bg-card border border-border/60 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <FileCheck className="size-5 text-chart-1" />
                <h4 className="font-serif font-semibold text-foreground text-lg">Unincorporated DeKalb</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Operates under broader county zoning classifications and inspection schedules. While structural and mechanical
                submittals follow uniform state codes, administrative intake schedules and review cycles differ.
              </p>
              <ul className="text-xs text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>County-level zoning verification and setback audits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>Separate watershed management clearances for additions</span>
                </li>
              </ul>
            </div>

            {/* Column 3: Historic District Reviews */}
            <div className="p-6 rounded-lg bg-card border border-border/60 shadow-2xs">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="size-5 text-chart-1" />
                <h4 className="font-serif font-semibold text-foreground text-lg">Historic Preservation</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Properties within designated historic overlays (such as MAK, McDonough-Adams-Kings) require Certificates of
                Appropriateness (COA) evaluated by the Historic Preservation Commission.
              </p>
              <ul className="text-xs text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>Strict architectural guidelines for visible elevations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="size-1.5 rounded-full bg-chart-1 mt-1.5 shrink-0" />
                  <span>Window profile, siding, and roofline historical conformity</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contingency Callout Banner */}
        <div className="rounded-xl border border-chart-1/30 bg-card p-8 sm:p-10 shadow-sm relative overflow-hidden">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-chart-1 font-medium text-sm mb-2">
                <ShieldAlert className="size-4" />
                <span>Financial Best Practice</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mb-3">
                The Vital Role of a 10% to 15% Project Contingency
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-4">
                Even with thorough investigative demolition and detailed architectural drawings, vintage structures hide
                undocumented modifications behind closed walls. Establishing an unallocated reserve of ten to fifteen percent
                safeguards project momentum when encountering concealed structural anomalies, outdated plumbing, or unvented framing.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                A dedicated contingency ensures unexpected discovery items are resolved with sound structural craftsmanship rather
                than compromising on your primary design vision or selected finish quality.
              </p>
            </div>

            <div className="w-full lg:w-auto shrink-0 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ProcessPlanningPage">
                  <span>Explore Our Planning Process</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  <span>Discuss Your Project</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}